export interface RetryAfterError extends Error {
  retryAfterMs?: number;
}

export function retryAfterMsFromHeader(value: unknown): number | undefined {
  if (value === undefined || value === null) return undefined;
  const raw = Array.isArray(value) ? value[0] : value;
  if (typeof raw !== 'string' || raw.length === 0) return undefined;

  const seconds = Number(raw);
  if (Number.isFinite(seconds) && seconds >= 0) return seconds * 1000;

  const dateMs = Date.parse(raw);
  if (!Number.isFinite(dateMs)) return undefined;
  return Math.max(0, dateMs - Date.now());
}

export function headerValue(headers: unknown, name: string): unknown {
  if (!headers) return undefined;
  if (typeof (headers as { get?: unknown }).get === 'function') {
    return (headers as { get(name: string): unknown }).get(name);
  }

  const normalizedName = name.toLowerCase();
  if (Array.isArray(headers)) {
    for (const entry of headers) {
      if (!Array.isArray(entry) || entry.length < 2) continue;
      if (String(entry[0]).toLowerCase() === normalizedName) return entry[1];
    }
    return undefined;
  }

  if (typeof headers === 'object') {
    for (const [key, value] of Object.entries(headers as Record<string, unknown>)) {
      if (key.toLowerCase() === normalizedName) return value;
    }
  }
  return undefined;
}

/**
 * Find a retry delay on an error, walking `cause` outward until one is found.
 *
 * Iterative with a seen-set rather than recursive: `err.cause === err` is legal
 * (as is any longer cycle), and recursion would run until the stack overflowed.
 * Callers invoke this from inside their error handlers — `recordPollFailure()` in
 * the account-updates poll `catch`, `OstiumSubgraphClient` on a failed read — so a
 * `RangeError` there escapes as an unhandled rejection rather than as a normal
 * request failure. The loop also keeps a legitimately deep chain off the stack.
 */
export function retryAfterMsFromError(error: unknown): number | undefined {
  const seen = new WeakSet<object>();
  let current: unknown = error;

  while (current && typeof current === 'object') {
    if (seen.has(current)) return undefined;
    seen.add(current);

    const withRetry = current as { retryAfterMs?: unknown; response?: unknown; cause?: unknown };
    if (typeof withRetry.retryAfterMs === 'number' && Number.isFinite(withRetry.retryAfterMs)) {
      return Math.max(0, withRetry.retryAfterMs);
    }

    const response = withRetry.response as { headers?: unknown } | undefined;
    const retryAfterMs = retryAfterMsFromHeader(headerValue(response?.headers, 'retry-after'));
    if (retryAfterMs !== undefined) return retryAfterMs;

    current = withRetry.cause;
  }

  return undefined;
}
